// src/services/userService.js
import api from "./api";

// ========== ADMIN USER MANAGEMENT ==========

const getUsersForAdmin = async (queryParams) => {
  const response = await api.get(`/admin-user`, { params: queryParams });
  return response.data;
};

const approveRejectMultipleUsers = async (userData) => {
  const response = await api.put(
    `/admin-user/approve-reject-multiple`,
    userData
  );
  return response.data;
};

const blockUnblockMultipleUsers = async (userData) => {
  const response = await api.put(
    `/admin-user/block-unblock-multiple`,
    userData
  );
  return response.data;
};

const deleteMultipleUsers = async (userData) => {
  const response = await api.delete(`/admin-user/delete-multiple`, {
    data: { userIds: userData },
  });
  return response.data;
};

const getUserRequests = async (queryParams) => {
  const response = await api.get(`/admin-user/user-requests`, {
    params: queryParams,
  });
  return response.data;
};

const handleUserRequest = async (requestData) => {
  const response = await api.post(
    `/admin-user/user-requests/handle`,
    requestData
  );
  return response.data;
};

const deleteUserRequest = async (requestId) => {
  const response = await api.delete(`/admin-user/user-requests/${requestId}`);
  return response.data;
};

const deleteMultipleUserRequests = async (requestIds) => {
  const response = await api.delete(`/admin-user/user-requests`, {
    data: { requestIds },
  });
  return response.data;
};

const createUserByAdmin = async (userData) => {
  const response = await api.post(`/admin-user/create-user`, { userData });
  return response.data;
};

const updateUserByAdmin = async (userId, userData) => {
  const response = await api.put(`/admin-user/update-user/${userId}`, {
    userData,
  });
  return response.data;
};

// ========== ADMIN APPOINTMENT ANALYTICS ==========

// Get specific user's appointment statistics
const getUserAppointmentStatistics = async (userId) => {
  const response = await api.get(
    `/admin-user-appointment-analytics/users/${userId}/statistics`
  );
  return response.data;
};

// Get specific user's appointment insights
const getUserAppointmentInsights = async (userId) => {
  const response = await api.get(
    `/admin-user-appointment-analytics/users/${userId}/insights`
  );
  return response.data;
};

// Get specific user's behavior analysis
const getUserBehaviorAnalysis = async (userId) => {
  const response = await api.get(
    `/admin-user-appointment-analytics/users/${userId}/behavior-analysis`
  );
  return response.data;
};

// Get aggregated statistics across all users
const getAggregateStatistics = async (filters) => {
  const response = await api.get(
    `/admin-user-appointment-analytics/statistics/aggregate`,
    { params: filters }
  );
  return response.data;
};

// Get top customers by appointments
const getTopCustomersByAppointments = async (params) => {
  const response = await api.get(
    `/admin-user-appointment-analytics/customers/top`,
    { params }
  );
  return response.data;
};

// Get high-risk customers (likely to no-show)
const getHighRiskCustomers = async (params) => {
  const response = await api.get(
    `/admin-user-appointment-analytics/customers/high-risk`,
    { params }
  );
  return response.data;
};

// Get VIP customers
const getVIPCustomers = async (params) => {
  const response = await api.get(
    `/admin-user-appointment-analytics/customers/vip`,
    { params }
  );
  return response.data;
};

// Get branch performance comparison
const getBranchPerformance = async () => {
  const response = await api.get(
    `/admin-user-appointment-analytics/branch-performance`
  );
  return response.data;
};

// ========== USER BRANCH MANAGEMENT (ADMIN VIEW) ==========

// Get user's branch preference
const getUserBranchPreferences = async (userId) => {
  const response = await api.get(`/admin-user/${userId}/branches/preferences`);
  return response.data;
};

const updateUserPrimaryBranch = async (userId, branchData) => {
  const response = await api.put(`/admin-user/${userId}/branches/primary`, {
    branchId: branchData.branchId,
    branchCode: branchData.branchCode,
    branchName: branchData.branchName,
    isAutoSelected: branchData.isAutoSelected || false,
  });
  return response.data;
};

// ========== USER PROFILE & VISIT HISTORY ==========

// Get user's visit history
const getUserVisitHistory = async (userId, branchCode = null) => {
  const params = branchCode ? { branchCode } : {};
  // Changed from /user-service to /admin-user
  const response = await api.get(`/admin-user/${userId}/visits`, { params });
  return response.data;
};

// Get user's purchase history
const getUserPurchaseHistory = async (userId) => {
  // Changed from /user-service to /admin-user
  const response = await api.get(`/admin-user/${userId}/purchases`);
  return response.data;
};

// Get user's customer profile
const getUserCustomerProfile = async (userId) => {
  // Changed from /user-service to /admin-user
  const response = await api.get(`/admin-user/${userId}/profile`);
  return response.data;
};

// Update user's customer profile
const updateUserCustomerProfile = async (userId, profileData) => {
  // Changed from /user-service to /admin-user
  const response = await api.put(`/admin-user/${userId}/profile`, profileData);
  return response.data;
};

// Get user's loyalty information
const getUserLoyaltyInfo = async (userId) => {
  // Changed from /user-service to /admin-user
  const response = await api.get(`/admin-user/${userId}/loyalty`);
  return response.data;
};

// Update user's loyalty points
const updateUserLoyaltyPoints = async (userId, pointsData) => {
  // Changed from /user-service to /admin-user
  const response = await api.post(
    `/admin-user/${userId}/loyalty/points`,
    pointsData
  );
  return response.data;
};

// ========== BULK OPERATIONS ==========

// Get multiple users by IDs
const getBulkUsers = async (userIds) => {
  // Changed from /user-service to /admin-user
  const response = await api.post(`/admin-user/bulk/details`, { userIds });
  return response.data;
};

// ========== APPOINTMENT STATISTICS (ADMIN) ==========

// Update user appointment statistics
const updateUserAppointmentStats = async (userId, statsUpdate) => {
  const response = await api.put(
    `/admin-user/${userId}/stats/appointments`,
    statsUpdate
  );
  return response.data;
};

// ========== USER ANALYTICS DASHBOARD ==========

// Get comprehensive user analytics for dashboard
const getUserAnalytics = async (userId) => {
  try {
    const [
      statistics,
      insights,
      behavior,
      visitHistory,
      purchaseHistory,
      loyalty,
    ] = await Promise.all([
      getUserAppointmentStatistics(userId),
      getUserAppointmentInsights(userId),
      getUserBehaviorAnalysis(userId),
      getUserVisitHistory(userId),
      getUserPurchaseHistory(userId),
      getUserLoyaltyInfo(userId),
    ]);

    return {
      success: true,
      data: {
        statistics: statistics.data,
        insights: insights.data,
        behavior: behavior.data,
        visitHistory: visitHistory.data,
        purchaseHistory: purchaseHistory.data,
        loyalty: loyalty.data,
      },
    };
  } catch (error) {
    throw error;
  }
};

// ========== KYC VERIFICATION (NEW) ==========

const verifyUserKYC = async (userId, verified) => {
  const response = await api.post(
    `/admin-user/users/${userId}/kyc-verification`,
    {
      verified,
    }
  );
  return response.data;
};

const userService = {
  // User Management
  getUsersForAdmin,
  approveRejectMultipleUsers,
  blockUnblockMultipleUsers,
  deleteMultipleUsers,
  createUserByAdmin,
  updateUserByAdmin,

  // User Requests
  getUserRequests,
  handleUserRequest,
  deleteUserRequest,
  deleteMultipleUserRequests,

  // Appointment Analytics
  getUserAppointmentStatistics,
  getUserAppointmentInsights,
  getUserBehaviorAnalysis,
  getAggregateStatistics,
  getTopCustomersByAppointments,
  getHighRiskCustomers,
  getVIPCustomers,
  getBranchPerformance,

  // Branch Management
  getUserBranchPreferences,
  updateUserPrimaryBranch,

  // Visit & Purchase History
  getUserVisitHistory,
  getUserPurchaseHistory,
  getUserCustomerProfile,
  updateUserCustomerProfile,

  // Loyalty
  getUserLoyaltyInfo,
  updateUserLoyaltyPoints,

  // Bulk Operations
  getBulkUsers,

  // Stats Updates
  updateUserAppointmentStats,

  // Combined Analytics
  getUserAnalytics,
  verifyUserKYC,
};

export default userService;
